import { ArrowLeft, ArrowRight, FileText, Mail, Search, Sparkles, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCreateJob, useLeadTypes, useProject } from '../../hooks/queries';
import { agentOptionsFor, AGENT_OPTIONS } from '../../lib/agent-jobs';
import type { JobType } from '../../types/api';
import { QueueContentModal } from '../../pages/content/QueueContentModal';
import { Modal, useToast } from '../ui/overlays';
import { Button, EmptyState, ErrorBlock, Field, Input, LoadingBlock, Select, Textarea } from '../ui/primitives';
import { AgentHandoff } from './AgentHandoff';

const ICONS = { CONTENT: FileText, RESEARCH: Search, OUTREACH: Mail, ENRICH: Users, CUSTOM: Sparkles };

// Unmount the form on close so each launch starts with a fresh brief.
export const LaunchAgentModal = (props: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  initialType?: JobType;
  leadTypeId?: string;
  targetNewLeads?: number;
}) => props.open ? <LaunchAgentFlow {...props} /> : null;

const LaunchAgentFlow = ({ projectId, onClose, initialType, leadTypeId, targetNewLeads }: {
  projectId: string;
  onClose: () => void;
  initialType?: JobType;
  leadTypeId?: string;
  targetNewLeads?: number;
}) => {
  const { data: project, error, isLoading } = useProject(projectId);
  const [type, setType] = useState<JobType | undefined>(initialType);
  const [busy, setBusy] = useState(false);
  if (type === 'CONTENT') return <QueueContentModal open onClose={onClose} projectId={projectId} topicIds={[]} onBack={() => setType(undefined)} />;
  const option = AGENT_OPTIONS.find((item) => item.type === type);
  return (
    <Modal open onClose={() => { if (!busy) onClose(); }} title={option?.label ?? 'Launch an agent'}
      description={option?.description ?? 'Choose what you want to get done. We’ll prepare the job and help you start your agent.'}>
      {isLoading ? <LoadingBlock /> : error ? <ErrorBlock error={error} /> : !project ? null : type ? (
        <>
          <Button variant="ghost" size="sm" className="mb-4" disabled={busy} icon={<ArrowLeft className="h-4 w-4" />} onClick={() => setType(undefined)}>Agent types</Button>
          {type === 'OUTREACH' ? (
            <div className="space-y-4">
              <p className="text-[13px] leading-relaxed text-muted">Select the leads you want to contact, then choose “Queue outreach”. You can set the campaign brief, template, mailbox, and approval settings before queueing.</p>
              <Link to={`/projects/${projectId}/leads`} onClick={onClose} className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-medium text-brand-ink">Choose leads <ArrowRight className="h-4 w-4" /></Link>
            </div>
          ) : (
            <AgentJobForm key={type} projectId={projectId} type={type} leadTypeId={leadTypeId} targetNewLeads={targetNewLeads} onClose={onClose} onBusyChange={setBusy} />
          )}
        </>
      ) : (
        <div className="space-y-3">
          {agentOptionsFor(project.kind).map((item) => {
            const Icon = ICONS[item.type];
            return <button key={item.type} type="button" onClick={() => setType(item.type)} className="group flex w-full items-center gap-4 rounded-xl border border-line p-4 text-left transition-colors hover:border-brand/40 hover:bg-brand/5">
              <span className="rounded-lg bg-brand/10 p-2.5 text-brand"><Icon className="h-5 w-5" /></span>
              <span className="min-w-0 flex-1"><span className="block font-semibold text-ink">{item.label}</span><span className="mt-1 block text-[13px] text-muted">{item.description}</span></span>
              <ArrowRight className="h-4 w-4 shrink-0 text-faint group-hover:text-brand" />
            </button>;
          })}
        </div>
      )}
    </Modal>
  );
};

const AgentJobForm = ({ projectId, type, leadTypeId: initialLeadTypeId, targetNewLeads, onClose, onBusyChange }: {
  projectId: string;
  type: Exclude<JobType, 'CONTENT' | 'OUTREACH'>;
  leadTypeId?: string;
  targetNewLeads?: number;
  onClose: () => void;
  onBusyChange: (busy: boolean) => void;
}) => {
  const { data: leadTypes, isLoading, error } = useLeadTypes(type === 'CUSTOM' ? undefined : projectId);
  const createJob = useCreateJob(projectId);
  useEffect(() => { onBusyChange(createJob.isPending); }, [createJob.isPending, onBusyChange]);
  const toast = useToast();
  const [leadTypeId, setLeadTypeId] = useState(initialLeadTypeId ?? '');
  const [target, setTarget] = useState(String(targetNewLeads ?? 20));
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('5');
  const [queued, setQueued] = useState(false);
  const activeTypes = (leadTypes ?? []).filter((item) => item.isActive);
  const effectiveLeadTypeId = leadTypeId || activeTypes[0]?.id || '';
  const selectedType = activeTypes.find((item) => item.id === effectiveLeadTypeId);
  const validTarget = Number.isInteger(Number(target)) && Number(target) >= 1 && Number(target) <= 1000;
  if (queued) return <AgentHandoff projectId={projectId} type={type} summary="Job queued" onClose={onClose} />;
  if (isLoading && type !== 'CUSTOM') return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (type !== 'CUSTOM' && !activeTypes.length) return <EmptyState title="Add an active lead type first" description="The agent uses its research brief to understand which leads to work on." action={<Link className="text-brand hover:underline" to={`/projects/${projectId}/lead-types`} onClick={onClose}>Set up lead types</Link>} />;
  return (
    <form className="space-y-4" onSubmit={async (event) => {
      event.preventDefault();
      if (createJob.isPending || (type !== 'CUSTOM' && !selectedType) || (type === 'RESEARCH' ? !validTarget : notes.trim().length < 10)) return;
      try {
        await createJob.mutateAsync({
          type,
          ...(type !== 'CUSTOM' ? { leadTypeId: effectiveLeadTypeId } : {}),
          priority: Number(priority),
          payload: { ...(type === 'RESEARCH' ? { targetNewLeads: Number(target) } : {}), ...(notes.trim() ? { notes: notes.trim() } : {}) },
        });
        setQueued(true);
      } catch (caught) { toast.error(caught); }
    }}>
      <fieldset disabled={createJob.isPending} className="space-y-4">
        {type !== 'CUSTOM' ? <>
          <Field label="Lead type" htmlFor="launch-lead-type"><Select id="launch-lead-type" value={effectiveLeadTypeId} onChange={(event) => setLeadTypeId(event.target.value)}>{activeTypes.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</Select></Field>
          {selectedType ? type === 'RESEARCH' ? <details className="rounded-lg bg-raised p-3 text-xs leading-relaxed text-muted"><summary className="cursor-pointer font-medium text-ink">View research brief</summary><p className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap">{selectedType.researchPrompt}</p></details> : <p className="rounded-lg bg-raised p-3 text-xs leading-relaxed text-muted">Work on existing {selectedType.name} leads. Describe the missing details the agent should research below.</p> : null}
        </> : null}
        {type === 'RESEARCH' ? <Field label="New leads to find" htmlFor="launch-target"><Input id="launch-target" type="number" min={1} max={1000} step={1} required value={target} onChange={(event) => setTarget(event.target.value)} /></Field> : null}
        <Field label={type === 'RESEARCH' ? 'Extra instructions (optional)' : 'Agent brief'} htmlFor="launch-notes" hint={type === 'RESEARCH' ? 'Add a region, source, or focus for this run.' : 'Describe the scope, what to do, and what a successful result looks like.'}>
          <Textarea id="launch-notes" rows={5} maxLength={8000} minLength={type === 'RESEARCH' ? undefined : 10} required={type !== 'RESEARCH'} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder={type === 'ENRICH' ? 'Find verified contact emails and company websites for leads missing these details. Include source URLs.' : type === 'CUSTOM' ? 'Describe the task and the result you want the agent to produce…' : 'Focus on…'} />
        </Field>
        <Field label="Queue priority" htmlFor="launch-priority"><Select id="launch-priority" value={priority} onChange={(event) => setPriority(event.target.value)}><option value="1">High — run first</option><option value="5">Normal</option><option value="9">Low — run after other jobs</option></Select></Field>
      </fieldset>
      <p className="text-xs text-muted">Next, copy the run prompt to your connected agent. Track its progress on Jobs.</p>
      <Button type="submit" variant="primary" className="w-full" loading={createJob.isPending} disabled={type === 'RESEARCH' ? !selectedType || !validTarget : notes.trim().length < 10}>Queue {type === 'RESEARCH' ? 'research' : type === 'ENRICH' ? 'enrichment' : 'custom task'}</Button>
    </form>
  );
};
